import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Autocomplete, TextField } from '@mui/material'
import { Controller } from 'react-hook-form'
import { useGetVehiclesLazyQuery } from 'services/vehicles'
import { formatVehicle, getCustomerVehicles } from '../../../utils/data-manipulation/common'
import { useTranslation } from 'react-i18next'

const VehicleAutocomplete = ({
  name,
  control,
  customerVehicle,
  setCustomerVehicle,
  setValue,
  customerVehicleOptions,
  customerId,
  hasSupplier,
  ...restProps
}) => {
  const [getVehiclesLazyQuery, { data: vehiclesData, isLoading: isLoadingVehiclesData }] = useGetVehiclesLazyQuery()
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)
  const { t } = useTranslation('inboundTicket')

  const getVehicleOptions = () => {
    if (!customerId) return vehiclesData?.Items || []
    if (customerVehicleOptions.length === 1) return [customerVehicleOptions[0]]
    return customerVehicleOptions.length > 0 ? customerVehicleOptions : []
  }

  const vehicleOptions = useMemo(
    () => [
      ...(customerVehicle?.id ? [customerVehicle] : []),
      ...getCustomerVehicles(getVehicleOptions()).filter(vehicle => vehicle.id !== customerVehicle?.id),
    ],
    [vehiclesData, customerVehicle, customerVehicleOptions],
  )

  useEffect(() => {
    if (hasSupplier && customerVehicleOptions.length === 1) {
      setCustomerVehicle(formatVehicle(customerVehicleOptions[0]))
      setValue(name, customerVehicleOptions[0])
    }
    if (!hasSupplier && customerVehicleOptions.length > 0 && !customerVehicle?.id) {
      setValue(name, '')
      setCustomerVehicle(null)
    }
  }, [hasSupplier, customerVehicleOptions, name])

  useEffect(() => {
    if (!open) return

    getVehiclesLazyQuery(
      { filter: JSON.stringify({ plateNumber: inputValue }) },
      { skip: customerVehicleOptions.length > 0 },
    )
  }, [open, inputValue, customerVehicleOptions])

  return (
    <Controller
      name={name}
      control={control}
      render={() => (
        <Autocomplete
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          loading={isLoadingVehiclesData}
          options={vehicleOptions}
          value={customerVehicle}
          defaultValue={customerVehicle}
          getOptionLabel={option => option?.label || ''}
          isOptionEqualToValue={(option, value) => option?.label === value?.label}
          onChange={(_, vehicle) => {
            setCustomerVehicle(vehicle)
            setValue(name, vehicle, { shouldDirty: true })
            setInputValue(vehicle?.plateNumber || '')
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue)
          }}
          renderInput={params => <TextField {...params} label={t('vehicle')} />}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.label}
            </li>
          )}
          {...restProps}
        />
      )}
    />
  )
}

VehicleAutocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.instanceOf(Object).isRequired,
  customerVehicle: PropTypes.object,
  setCustomerVehicle: PropTypes.func,
  setValue: PropTypes.func,
  customerVehicleOptions: PropTypes.array,
  customerId: PropTypes.string,
  hasSupplier: PropTypes.bool,
}

VehicleAutocomplete.defaultProps = {
  customerVehicle: {},
  setCustomerVehicle: () => {},
  setValue: () => {},
  customerVehicleOptions: [],
  customerId: '',
  hasSupplier: false,
}

export default VehicleAutocomplete
